import { motion } from "motion/react";
import { Star, ArrowRight, Phone } from "lucide-react";
import { siteData } from "../data/mock";

export const HeroSection = () => {
  const { hero, clinic } = siteData;

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      aria-label="Hero - Expert Healing Tailored to You"
      className="relative min-h-screen flex items-center justify-center text-center px-5 md:px-9 overflow-hidden"
      style={{
        background: `linear-gradient(180deg,
          rgba(252, 202, 199, 0.8) 0%,
          rgba(253, 215, 197, 0.6) 25%,
          rgba(254, 241, 229, 0.5) 50%,
          rgba(255, 250, 243, 0.7) 75%,
          rgba(255, 249, 242, 1) 100%
        )`,
      }}
    >
      {/* Decorative circles */}
      <div
        className="absolute top-[15%] left-[10%] w-[300px] h-[300px] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "rgba(252, 202, 199, 0.6)" }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-[20%] right-[8%] w-[250px] h-[250px] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: "rgba(184, 209, 186, 0.5)" }}
        aria-hidden="true"
      />

      <div className="max-w-[800px] mx-auto relative z-10 pt-24 pb-16">
        {/* Yelp badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2.5 rounded-full px-4 py-2 mb-8"
          style={{
            background: "rgba(255, 255, 255, 0.4)",
            backdropFilter: "blur(4px)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} fill="#BCA182" stroke="#BCA182" />
            ))}
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-[#353535]">
            {clinic.rating} on {clinic.ratingSource}
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-5"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(2.5rem, 6vw, 4rem)",
            lineHeight: 1.08,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "#232323",
          }}
        >
          {hero.headline}
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-[560px] mx-auto mb-10"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "clamp(1rem, 2vw, 1.15rem)",
            lineHeight: 1.65,
            color: "#353535",
          }}
        >
          {hero.subheadline}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => scrollTo("#contact")}
            className="inline-flex items-center gap-2 bg-[#232323] text-white border-none rounded-full px-7 py-3.5 font-mono text-[13px] font-medium uppercase tracking-wide cursor-pointer hover:bg-[#353535] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 w-full sm:w-auto justify-center"
          >
            {hero.ctaPrimary}
            <ArrowRight size={15} strokeWidth={2} />
          </button>
          <a
            href={clinic.phoneLink}
            className="inline-flex items-center gap-2 text-[#232323] border rounded-full px-7 py-3.5 font-mono text-[13px] font-medium uppercase tracking-wide no-underline hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 w-full sm:w-auto justify-center"
            style={{
              background: "rgba(255,255,255,0.2)",
              borderColor: "rgba(0,0,0,0.1)",
              backdropFilter: "blur(4px)",
            }}
          >
            <Phone size={15} strokeWidth={2} />
            {hero.ctaSecondary}
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 rounded-full flex justify-center pt-2.5"
          style={{ border: "1.5px solid rgba(0,0,0,0.15)" }}
        >
          <div className="w-1 h-2 rounded-full" style={{ background: "rgba(0,0,0,0.25)" }} />
        </motion.div>
      </motion.div>
    </section>
  );
};
